import React from 'react'
import { colors, midas } from '@resurs/midas'
import { NavLink } from 'react-router-dom'
import { Authorities } from '../../../models/authorities'
import { Roles } from '../../../models/roles'
import { getText } from '../../TextLabels'
import {
  Features,
  isEnabled,
  isEnabledForStoreOrRepresentativeId,
  isEnabledForValue,
} from '../../../models/features'
import useModuleMenu from '../../../hooks/useModuleMenu'
import { generateMenuItems, getTransactionMenuItems } from './structureUtils'
import { modules } from '../../../../modules'

const LeftSideStructure = (
  userAuthorities,
  role,
  router,
  unreadNewsItems,
  isError,
  isBonusChecksActivated,
  storeId,
  representativeId,
  countryCode,
  delayedPaymentEnabled,
) => {
  const currentRoute = route => route === router.location.pathname
  const tooltip = getText('menu.main.news.title')

  const newsMenuTitle = () => {
    if (isError || unreadNewsItems > 0) {
      const badge = {
        color: '#fff',
        background: colors.primaryMain,
        width: 'auto',
        height: 'auto',
        margin: '1px',
        borderRadius: '50%',
        position: 'relative',
        top: '-8px',
        right: '0px',
        padding: '1px 6px 1px 6px',
        fontWeight: 'bold',
        fontSize: '0.8em',
        textShadow: 'none',
      }

      const errorBadge = {
        ...badge,
        color: '#fff',
        background: '#f00',
        borderRadius: '0%',
      }

      return (
        <>
          <span className={isError ? midas.disabled : ''}>{tooltip}</span>
          <span
            style={isError ? errorBadge : badge}
            className={
              isError ? [midas.tooltip, midas.tooltipDanger].join(' ') : ''
            }
            data-tooltip={getText('menu.main.news.error')}
          >
            {isError ? '!' : unreadNewsItems}
          </span>
        </>
      )
    }
    return tooltip
  }

  const navPermissions = []

  const settingsPermissions = []

  if (!isError && role === Roles.RESURS_ADMIN) {
    navPermissions.push({
      key: 'news',
      icon: 'midasMail2',
      value: newsMenuTitle(),
      tooltip,
      items: [
        {
          key: 'create',
          value: (
            <NavLink to="/news/admin/edit">
              {getText('menu.main.news.create')}
            </NavLink>
          ),
        },
        {
          key: 'show',
          value: <NavLink to="/news">{getText('menu.main.news.show')}</NavLink>,
        },
        {
          key: 'edit',
          value: (
            <NavLink to="/news/admin">{getText('menu.main.news.edit')}</NavLink>
          ),
        },
      ],
    })
  } else {
    navPermissions.push({
      key: 'news',
      icon: 'midasMail2',
      value: newsMenuTitle(),
      tooltip,
      route: isError ? '' : '/news',
    })
  }

  userAuthorities.map(authority => {
    if (
      authority === Authorities.PAY_APPLY &&
      (delayedPaymentEnabled ||
        isEnabledForValue(
          Features.ACTIVATED_COUNTRIES_FOR_DELAYED_PAYMENT,
          countryCode,
        ) ||
        isEnabledForStoreOrRepresentativeId(
          Features.ACTIVATED_REPRESENTATIVES_FOR_DELAYED_PAYMENT,
          storeId,
          representativeId,
        ) ||
        isEnabledForValue(
          Features.ACTIVATED_COUNTRIES_FOR_PUSH_OUT,
          countryCode,
        ) ||
        isEnabledForStoreOrRepresentativeId(
          Features.ACTIVATED_STORES_FOR_DELAYED_PAYMENT,
          storeId,
          representativeId,
        ))
    ) {
      navPermissions.unshift({
        key: 'ongoing',
        icon: 'midasCheckList',
        value: getText('menu.main.ongoingPayments'),
        route: '/payments/ongoing',
        active: currentRoute('/payments/ongoing'),
      })
    }

    if (authority === Authorities.PAY_APPLY) {
      navPermissions.unshift({
        key: 'overview',
        icon: 'midasSignNew2',
        value: getText('menu.main.newApplication'),
        route: '/apply',
        active: currentRoute('/apply'),
      })
    }

    if (authority === Authorities.TRANSACTIONS) {
      const transactions = getTransactionMenuItems(
        userAuthorities,
        representativeId,
        currentRoute,
        router,
      ).filter(entry => entry != null)

      transactions.forEach(entry => {
        navPermissions.push(entry)
      })
    }

    if (authority === Authorities.STATISTICS) {
      navPermissions.push({
        key: authority.toLowerCase(),
        icon: 'midasPieChart1',
        value: getText('menu.main.statistics'),
        route: '/statistics',
        active: currentRoute('/statistics'),
      })
    }

    if (authority === Authorities.SUPPORT) {
      navPermissions.push({
        key: authority.toLowerCase(),
        icon: 'midasBubbleAsk2',
        value: getText('menu.main.support.title'),
        active: currentRoute('/support'),
        items: [
          {
            key: 'faq',
            value: (
              <NavLink to="/support/faq">
                {getText('menu.main.support.faq')}
              </NavLink>
            ),
          },
          {
            key: 'documents',
            value: (
              <NavLink to="/support/docs">
                {getText('menu.main.support.docs')}
              </NavLink>
            ),
          },
          {
            key: 'order-material',
            value: (
              <NavLink to="/support/order">
                {getText('menu.main.support.order')}
              </NavLink>
            ),
          },
        ],
      })
    }

    if (authority === Authorities.USER_ADMINISTRATION) {
      settingsPermissions.push({
        key: authority.toLowerCase(),
        value: (
          <NavLink to="/users">{getText('menu.main.settings.user')}</NavLink>
        ),
      })
    }

    if (authority === Authorities.SETTINGS) {
      settingsPermissions.push({
        key: authority.toLowerCase(),
        value: (
          <NavLink to="/settings/store">
            {getText('menu.main.settings.company')}
          </NavLink>
        ),
      })
      settingsPermissions.push({
        key: 'payment-settings',
        value: (
          <NavLink to="/settings/payment">
            {getText('menu.main.settings.payment')}
          </NavLink>
        ),
      })
    }

    if (authority === Authorities.SET_STORE) {
      settingsPermissions.push({
        key: authority.toLowerCase(),
        value: (
          <NavLink to="/store/change">
            {getText('menu.main.settings.changeStore')}
          </NavLink>
        ),
      })
    }
    return authority
  })

  // Economy
  const economyItems = []
  const reconciliationMenuData = useModuleMenu(
    modules.reconciliationApp,
    userAuthorities,
  )
  if (
    isEnabledForValue(Features.ACTIVATED_STORES_FOR_RECONCILIATION, storeId) &&
    reconciliationMenuData
  ) {
    economyItems.push(
      ...generateMenuItems(userAuthorities, reconciliationMenuData?.menuItems),
    )
  }

  if (economyItems.length) {
    navPermissions.splice(2, 0, {
      key: 'economy',
      icon: 'midasCalculator1',
      value: getText('menu.main.economy'),
      active: currentRoute('/economy'),
      items: economyItems,
    })
  }

  if (
    userAuthorities.includes(Authorities.SETTINGS) ||
    userAuthorities.includes(Authorities.USER_ADMINISTRATION)
  ) {
    settingsPermissions.push({
      key: 'settings-history',
      value: (
        <NavLink to="/settings/history">
          {getText('menu.main.settings.history')}
        </NavLink>
      ),
    })
  }

  if (
    role === Roles.SUPER_USER ||
    role === Roles.STORE_MANAGER ||
    role === Roles.RESURS_ADMIN
  ) {
    settingsPermissions.push({
      key: 'apply-links',
      value: (
        <NavLink to="/settings/apply-links">
          {getText('menu.main.settings.apply-links')}
        </NavLink>
      ),
    })
  }

  if (role === Roles.RESURS_ADMIN) {
    settingsPermissions.push({
      key: 'change-user-view',
      value: (
        <NavLink to="/change-user-view">
          {getText('menu.main.settings.changeUserView')}
        </NavLink>
      ),
    })
  }

  if (
    userAuthorities.includes(Authorities.PAY_APPLY) &&
    isBonusChecksActivated
  ) {
    navPermissions.push({
      key: 'bonuschecks',
      icon: 'midasDatabase',
      value: getText('menu.main.bonusChecks'),
      route: '/bonuschecks',
      active: currentRoute('/bonuschecks'),
    })
  }

  if (settingsPermissions.length > 0) {
    navPermissions.push({
      key: 'mp-settings',
      icon: 'midasSettingGear',
      value: getText('menu.main.settings.title'),
      items: settingsPermissions,
    })
  }
  return navPermissions.filter(nav => !nav.feature || isEnabled(nav.feature))
}

export default LeftSideStructure
